import { Fragment, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

import type { ReviewType } from "../../types";
import { ArrowDownIcon } from "./icons";
import Images from "./images";
import ReviewComponent from "./review-component";

interface ReviewContentProps {
  isFullReview: boolean;
  review: ReviewType;
}

export default function ReviewContent({
  isFullReview,
  review: { components, images, summary },
}: ReviewContentProps) {
  const summaryRef = useRef<HTMLParagraphElement>(null);
  const [showMoreButton, setShowMoreButton] = useState(false);

  useEffect(() => {
    if (isFullReview) {
      return;
    }

    let isCollapsed = false;

    if (summaryRef.current) {
      const { clientHeight, scrollHeight } = summaryRef.current;
      isCollapsed = scrollHeight > clientHeight;
    }

    if (components.length) {
      isCollapsed = true;
    }

    setShowMoreButton(isCollapsed);
  }, [isFullReview, components]);

  return (
    <div>
      {!!summary && (
        <p
          className={cn(
            "whitespace-pre-line text-14/[1.71em] text-white/80 md:text-white",
            !isFullReview && "line-clamp-3"
          )}
          ref={summaryRef}
        >
          {summary}
        </p>
      )}
      {!!(images || []).length && <Images images={images || []} />}
      {!!components.length && (
        <div
          className={cn(
            "relative mt-0.5 flex flex-col items-stretch overflow-hidden",
            !isFullReview &&
              "mask-linear-180 mask-linear-from-black mask-linear-to-transparent h-18.5"
          )}
        >
          {components.map((component, index) => (
            <Fragment key={component.id}>
              {!!index && <Separator className="bg-white/10" />}
              <ReviewComponent component={component} />
            </Fragment>
          ))}
        </div>
      )}
      {showMoreButton && (
        <Button className="mt-3 flex h-6 items-center gap-2 font-semibold text-14 text-white">
          Show more <ArrowDownIcon />
        </Button>
      )}
    </div>
  );
}
